import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CalendarClock,
  ClipboardList,
  Activity,
  Trophy,
  Tag,
  ChevronRight,
} from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { eventTeamApiService } from '../../../services/eventTeamApi';
import { usernameCacheService } from '../../../services/usernameCache';
import type {
  EventItem,
  EventTag,
  EventTeamStats,
  EventUpcomingTask,
} from '../../../types/eventTeam';
import { EventStatsCards } from '../EventStatsCards';
import { EventStatusBadge } from '../EventStatusBadge';
import { EventPriorityBadge } from '../EventPriorityBadge';
import { EventActivityLog } from '../EventActivityLog';
import { EventTagChip } from '../EventTagChip';
import { PlayerProfile } from '../../PlayerProfile';
import { cn } from '../../../utils/cn';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';

export interface EventDashboardPanelProps {
  /** Jump to the Events tab pre-filtered by tag (dashboard tag chips — guide §1.1). */
  onTagClick: (tagId: string) => void;
}

export const EventDashboardPanel = ({ onTagClick }: EventDashboardPanelProps) => {
  const navigate = useNavigate();

  const [stats, setStats] = useState<EventTeamStats | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<EventItem[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<EventUpcomingTask[]>([]);
  const [tags, setTags] = useState<EventTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.allSettled([
      eventTeamApiService.getStats(),
      eventTeamApiService.listUpcomingEvents(14, 10),
      eventTeamApiService.listUpcomingTasks(7),
      eventTeamApiService.listTags(),
    ]).then(([statsRes, eventsRes, tasksRes, tagsRes]) => {
      if (cancelled) return;
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value);
        const uuids = statsRes.value.points_leaderboard_top?.map((r) => r.member_uuid) ?? [];
        if (uuids.length > 0) usernameCacheService.prefetchUsernames(uuids);
      }
      if (eventsRes.status === 'fulfilled') setUpcomingEvents(eventsRes.value);
      if (tasksRes.status === 'fulfilled') {
        setUpcomingTasks(tasksRes.value);
        const uuids = tasksRes.value.map((t) => t.assignee_uuid).filter((u): u is string => !!u);
        if (uuids.length > 0) usernameCacheService.prefetchUsernames(uuids);
      }
      if (tagsRes.status === 'fulfilled') setTags(tagsRes.value);
      const firstError = [statsRes, eventsRes, tasksRes, tagsRes].find(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );
      if (firstError) {
        setError(
          firstError.reason instanceof Error
            ? firstError.reason.message
            : 'Failed to load part of the dashboard',
        );
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="glass-card p-4 space-y-2">
              <SkeletonLoader className="h-3 w-20" />
              <SkeletonLoader className="h-7 w-16" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="glass-card p-4 md:p-5 space-y-3">
              <SkeletonLoader className="h-4 w-40" />
              <SkeletonLoader className="h-12 w-full" />
              <SkeletonLoader className="h-12 w-full" />
              <SkeletonLoader className="h-12 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      <EventStatsCards stats={stats} />

      {/* Tag filter chips */}
      {tags.length > 0 && (
        <div className="glass-card p-3 flex items-center gap-2 flex-wrap">
          <span className="eyebrow inline-flex items-center gap-1 mr-1">
            <Tag className="w-3.5 h-3.5" />
            Tags
          </span>
          {tags.map((tag) => (
            <EventTagChip key={tag.id} tag={tag} onClick={() => onTagClick(tag.id)} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Upcoming events strip */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader
            title="Upcoming Events"
            icon={CalendarClock}
            iconClassName="text-cyan-400"
            description="Next 14 days"
          />
          {upcomingEvents.length === 0 ? (
            <EmptyState bare compact icon={CalendarClock} title="Nothing scheduled in the next 14 days." />
          ) : (
            <div className="divide-y divide-glass-200/10">
              {upcomingEvents.map((event) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => navigate(`/event-team/events/${event.id}`)}
                  className="w-full flex items-center justify-between gap-3 px-2 py-2.5 rounded-md hover:bg-glass-200/5 transition-colors text-left"
                >
                  <div className="min-w-0">
                    <span className="text-sm text-white font-medium truncate block">{event.title}</span>
                    <span className="text-xs text-glass-400 tabular-nums">
                      {event.scheduled_start
                        ? `${format(new Date(event.scheduled_start), 'EEE MMM d, HH:mm')}${
                            event.scheduled_end
                              ? ` → ${format(new Date(event.scheduled_end), 'HH:mm')}`
                              : ''
                          }`
                        : 'No run window'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <EventStatusBadge status={event.status} type="event" />
                    <EventPriorityBadge priority={event.priority} />
                    <ChevronRight className="w-4 h-4 text-glass-500" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming tasks strip */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader
            title="Upcoming Tasks"
            icon={ClipboardList}
            iconClassName="text-amber-400"
            description="Due in the next 7 days"
          />
          {upcomingTasks.length === 0 ? (
            <EmptyState bare compact icon={ClipboardList} title="No tasks due in the next 7 days." />
          ) : (
            <div className="divide-y divide-glass-200/10">
              {upcomingTasks.map((task) => {
                const overdue = task.due_date != null && isPast(new Date(task.due_date));
                return (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => navigate(`/event-team/events/${task.event_id}`)}
                    className="w-full flex items-center justify-between gap-3 px-2 py-2.5 rounded-md hover:bg-glass-200/5 transition-colors text-left"
                  >
                    <div className="min-w-0">
                      <span className="text-sm text-white truncate block">{task.title}</span>
                      <span className="text-xs text-glass-400 truncate block">
                        {task.event_title || task.event_id}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {task.assignee_uuid && (
                        <PlayerProfile uuid={task.assignee_uuid} showUuid={false} inline />
                      )}
                      {task.due_date && (
                        <span className={cn('text-xs tabular-nums', overdue ? 'text-red-400' : 'text-glass-400')}>
                          {formatDistanceToNow(new Date(task.due_date), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Points leaderboard top 5 */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Points Leaderboard" icon={Trophy} iconClassName="text-yellow-400" />
          {!stats || stats.points_leaderboard_top.length === 0 ? (
            <EmptyState bare compact icon={Trophy} title="No points awarded." />
          ) : (
            <div className="divide-y divide-glass-200/10">
              {stats.points_leaderboard_top.map((row, i) => (
                <div
                  key={row.member_uuid}
                  className="flex items-center justify-between gap-2 px-2 py-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={cn(
                        'w-5 text-center text-sm font-semibold tabular-nums flex-shrink-0',
                        i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-600' : 'text-glass-500',
                      )}
                    >
                      {i + 1}
                    </span>
                    <PlayerProfile
                      uuid={row.member_uuid}
                      name={row.display_name ?? undefined}
                      showUuid={false}
                      inline
                    />
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-yellow-300 flex-shrink-0">{row.total}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Activity feed */}
        <div className="glass-card p-4 md:p-5 xl:col-span-2">
          <SectionHeader title="Recent Activity" icon={Activity} />
          <EventActivityLog source={{ kind: 'recent', limit: 50 }} enableActorDrilldown />
        </div>
      </div>
    </div>
  );
};
